import { Link } from 'react-router-dom';
import type { Trip } from '../lib/types';
import { WatercolorBloom } from './WatercolorBloom';
import { tripDurationDays, daysUntil } from '../lib/api';
import { IconGlobe } from './Icons';

interface Props { trip: Trip; }

const STATUS_LABEL: Record<Trip['status'], string> = {
  idea: 'IDEA',
  researching: 'RESEARCHING',
  planning: 'PLANNING',
  booked: 'BOOKED',
  in_progress: 'IN PROGRESS',
  completed: 'COMPLETED',
  archived: 'ARCHIVED',
};

const COVER_BLOOM: Record<string, 'magenta'|'cyan'|'uv'|'lime'|'yellow'|'duo'> = {
  neon_dream: 'duo',
  electric_horizon: 'cyan',
  night_pulse: 'uv',
  holographic_bloom: 'lime',
};

export function TripCard({ trip }: Props) {
  const days = tripDurationDays(trip);
  const until = daysUntil(trip.start_date);
  const isUpcoming = until !== null && until >= 0;
  const isActive = trip.status === 'in_progress';

  return (
    <Link to={`/trip/${trip.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
      <article className="card brackets" style={{ minHeight: 220, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <WatercolorBloom
          variant={COVER_BLOOM[trip.cover_color ?? 'neon_dream'] ?? 'duo'}
          size={300} opacity={0.35}
          style={{ position: 'absolute', top: -80, right: -80, zIndex: 0 }}
        />

        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
          <span className={`pill pill-${trip.status}`}>
            {isActive && <span className="status-dot live" />}{STATUS_LABEL[trip.status]}
          </span>
          {trip.pinned && <span className="label-bright">★ PINNED</span>}
        </header>

        <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 22, lineHeight: 1.1, textTransform: 'uppercase', margin: 0, position: 'relative', zIndex: 1 }}>
          {trip.title}
        </h3>

        {trip.summary && (
          <p className="body muted" style={{ margin: 0, position: 'relative', zIndex: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {trip.summary}
          </p>
        )}

        <footer style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, position: 'relative', zIndex: 1 }}>
          <div className="col" style={{ gap: 2 }}>
            <span className="label">{formatDateRange(trip.start_date, trip.end_date)}</span>
            {days !== null && <span className="label muted">{days} day{days===1?'':'s'}</span>}
          </div>
          {isUpcoming && until > 0 && (
            <div style={{ textAlign: 'right' }}>
              <div className="mono-num gradient-text" style={{ fontSize: 28, lineHeight: 1 }}>{until}</div>
              <div className="label">days out</div>
            </div>
          )}
          {until === 0 && (
            <span className="pill pill-in_progress"><span className="status-dot live" />TODAY</span>
          )}
          {until !== null && until < 0 && trip.status !== 'completed' && (
            <span className="label muted">{Math.abs(until)}d ago</span>
          )}
        </footer>
      </article>
    </Link>
  );
}

function formatDateRange(start?: string | null, end?: string | null): string {
  if (!start && !end) return 'No dates set';
  if (start && end) return `${fmtDateOnly(start)} → ${fmtDateOnly(end)}`;
  return fmtDateOnly((start ?? end) as string);
}

// Treat YYYY-MM-DD as a local date (avoid the JS "midnight UTC → previous day in PDT" bug).
function fmtDateOnly(d: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (!m) return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
